import React, { forwardRef } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/cn'

// Base label is 14px Halbfett (weight 600), centered both axes via flex so the
// text sits mid-height. Weight is 600 rather than body-3-emphasis's 700 because
// the app ships no proportional Sohne 700 face — 700 rounds up to Fett (800) and
// looks too heavy; 600 (Halbfett) is a real loaded weight and matches Figma's
// default button.
export const buttonVariants = cva(
  // cursor-pointer is explicit because Tailwind v4's preflight resets buttons
  // to cursor:default; CircleButton and the app's hand-rolled buttons already
  // opt back in. disabled:cursor-default keeps the arrow on disabled buttons
  // even inside a pointer-cursor parent (pointer-events-none alone just defers
  // to whatever cursor the parent sets).
  'inline-flex items-center justify-center gap-2 h-12 px-6 text-body-3 font-semibold leading-none rounded-edu-none transition-colors cursor-pointer disabled:cursor-default',
  {
    variants: {
      kind: {
        // Primary — white fill, dark label. The high-emphasis product CTA
        // (Figma "Primary"). Uses the button-primary-* semantic tokens.
        primary:
          'bg-edu-button-primary-bg-default text-edu-button-text hover:bg-edu-button-primary-bg-hover',
        default:
          'bg-edu-bg-tertiary text-edu-fg-primary hover:bg-edu-button-hover border border-edu-stroke-default',
        highlight:
          'bg-edu-bg-tertiary text-edu-accent-yellow hover:bg-edu-button-hover border border-edu-stroke-default',
        // Secondary — the Figma "Secondary" button (node 6865:36694): a #2f2f2f
        // fill (button-secondary-bg-default) that lightens to neutral-8 on
        // hover, white label. Uses the button-secondary-* tokens so it stays in
        // sync with Figma. No border — Figma's stroke is stroke-subtle, the same
        // value as the fill, so it's invisible.
        secondary:
          'bg-edu-button-secondary-bg-default text-edu-fg-primary hover:bg-edu-button-secondary-bg-hover',
      },
      // Size — `default` inherits the base (48px tall). `small` is the 32px
      // tall variant (Figma "Size=Small"); the label size is the same
      // body-3-emphasis as the base.
      size: {
        default: '',
        small: 'h-8 px-4',
      },
      disabled: {
        true: 'opacity-30 pointer-events-none',
        false: '',
      },
      softDisabled: {
        true: 'opacity-30 cursor-default',
        false: '',
      },
    },
    defaultVariants: {
      kind: 'default',
      size: 'default',
      disabled: false,
      softDisabled: false,
    },
  },
)

export type ButtonProps = React.ComponentPropsWithRef<'button'> &
  VariantProps<typeof buttonVariants> & {
    children: React.ReactNode
    /** Optional leading icon rendered before the label. */
    icon?: React.ReactNode
  }

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      kind,
      size,
      disabled,
      softDisabled,
      icon,
      className,
      onClick,
      ...props
    },
    ref,
  ) => (
    <button
      ref={ref}
      type='button'
      className={cn(
        buttonVariants({
          kind,
          size,
          disabled: !!disabled,
          softDisabled: !!softDisabled,
        }),
        className,
      )}
      disabled={disabled ?? false}
      aria-disabled={softDisabled ? true : undefined}
      onClick={softDisabled ? undefined : onClick}
      {...props}
    >
      {icon ? <span className='inline-flex shrink-0'>{icon}</span> : null}
      {children}
    </button>
  ),
)

Button.displayName = 'Button'
